import dealmasterLogo from "@/images/dealmaster-logo.png"
import LobbyGameList from "./LobbyGameList"
import { useAppDispatch, useAppSelector } from "@/store/hooks"
import { setState } from "@/store/globalSlice"
import { useNavigate } from "react-router-dom"
import { getUserCreds, ORIGINAL_GAME_STORE_LINK, socket } from "@/utils/utils"
import { useState, useEffect } from "react"
import QuickJoinStatus from "./QuickJoinStatus"
import LobbyPlayerPanel from "./LobbyPlayerPanel"
import SinglePlayerModal from "./SinglePlayerModal"
import { featureFlags, isDemoMode, QUICK_JOIN_MAP_ID } from "@mono/constants"
import classNames from "classnames"
import { addToastNotification } from "@/store/globalSlice"
import { exitApp, isAndroidApp } from "@/utils/backButton"

interface LobbyBodyProps {}

// A phone runs the game locked to landscape, so `sm:` (max-height: 499px) is
// the mobile case for this page: width to spare, almost no height. Every mobile
// step below is written in raw px rather than rem, because index.css halves the
// root font size at phone widths — rem-based sizes shrink exactly where the
// screen is already smallest, which is what left this page's text a handful of
// pixels tall on a phone. Same reason the Navbar sizes itself in px.

// Dims a mode button and stamps a clear "Full Version" lock badge — used in demo mode.
const DemoLockOverlay: React.FC = () => (
  <>
    <span className="pointer-events-none absolute inset-0 rounded-xl bg-black/25" />
    <span className="pointer-events-none absolute -top-2.5 right-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-amber-300/50 bg-gray-950 px-2.5 py-1 text-[10px] font-extrabold uppercase tracking-wider text-amber-300 shadow-lg shadow-black/50">
      <i className="fas fa-lock text-[9px]" />
      Full Version
    </span>
  </>
)

interface ModeButtonProps {
  // "hero" is the one mode we want played most; everything else is a tile in
  // the grid under it. Same component either way so the primary mode can swap
  // (multiplayer normally, single player in demo) without duplicating markup.
  variant: "hero" | "tile"
  icon: string
  label: string
  subtitle?: string
  // Base gradient/border/ring classes, and the hover half kept separate so a
  // dimmed button does not light up under the cursor.
  accent: string
  accentHover: string
  onClick: () => void
  disabled?: boolean
  // Demo mode: still clickable (the click explains why it is unavailable), but
  // it reads as locked.
  locked?: boolean
  badge?: React.ReactNode
  testId?: TestId
}

const ModeButton: React.FC<ModeButtonProps> = ({
  variant,
  icon,
  label,
  subtitle,
  accent,
  accentHover,
  onClick,
  disabled,
  locked,
  badge,
  testId,
}) => {
  const isHero = variant === "hero"
  const dimmed = disabled || locked

  return (
    <button
      className={classNames(
        "group relative flex border text-left text-white shadow-lg backdrop-blur-sm transition-all duration-200 focus:outline-none focus:ring-2 active:scale-100",
        isHero
          ? "w-full items-center gap-5 rounded-2xl px-7 py-6 sm:gap-[10px] sm:rounded-[14px] sm:px-[12px] sm:py-[10px]"
          : "flex-col items-start gap-3 rounded-xl px-4 py-4 sm:gap-[6px] sm:rounded-[12px] sm:px-[10px] sm:py-[9px]",
        accent,
        dimmed
          ? "cursor-not-allowed opacity-60 grayscale"
          : `${accentHover} hover:-translate-y-0.5 hover:scale-[1.02]`
      )}
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
    >
      <span
        className={classNames(
          "flex flex-shrink-0 items-center justify-center rounded-lg bg-white/15",
          isHero
            ? "h-14 w-14 sm:h-[38px] sm:w-[38px]"
            : "h-10 w-10 sm:h-[30px] sm:w-[30px]"
        )}
      >
        <i
          className={classNames(
            "fas text-white",
            icon,
            isHero ? "text-2xl sm:text-[19px]" : "text-lg sm:text-[15px]"
          )}
        />
      </span>

      <span className={classNames("flex flex-col", isHero && "flex-1")}>
        <span className="flex flex-wrap items-center gap-2 sm:gap-[6px]">
          <span
            className={classNames(
              "font-semibold leading-tight",
              isHero ? "text-3xl sm:text-[19px]" : "text-lg sm:text-[14px]"
            )}
          >
            {label}
          </span>
          {badge}
        </span>
        {/* The subtitle is the first thing to go on a phone: the column is
            narrow enough there that a sentence wraps three ways, and the icon
            plus the label already say what the button does. It comes back on a
            short desktop window, where the column is wide. */}
        {isHero && subtitle && (
          <span className="mt-1 text-sm text-white/60 sm:hidden xl:block xl:text-xs">
            {subtitle}
          </span>
        )}
      </span>

      {locked ? (
        <i
          className={classNames(
            "fas fa-lock text-white/80",
            isHero ? "text-base" : "absolute right-3 top-3.5 text-xs"
          )}
        />
      ) : (
        isHero && (
          <i className="fas fa-chevron-right text-sm text-white/40 transition-transform duration-200 group-hover:translate-x-1 group-hover:text-white/70 sm:hidden xl:inline-block" />
        )
      )}
      {locked && <DemoLockOverlay />}
    </button>
  )
}

// The full version's store link. Takes the games panel's slot in demo mode,
// where that panel would never have anything in it to show.
const WishlistCard: React.FC = () => (
  <a
    href={ORIGINAL_GAME_STORE_LINK}
    target="_blank"
    rel="noreferrer"
    className="group flex h-fit w-[34rem] max-w-[90vw] items-center gap-4 rounded-2xl border border-amber-400/40 bg-amber-500/10 px-5 py-3.5 text-left text-amber-100 backdrop-blur-md transition-all duration-200 hover:border-amber-300/60 hover:bg-amber-500/15 sm:w-auto sm:min-w-0 sm:max-w-[320px] sm:flex-1 sm:gap-[10px] sm:px-[12px] sm:py-[10px] xl:w-[32rem] xl:max-w-[90vw] xl:flex-none"
  >
    <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-amber-400/15 ring-1 ring-amber-300/30 sm:h-[30px] sm:w-[30px]">
      <i className="fab fa-steam text-base text-amber-300 sm:text-[15px]" />
    </span>
    <span className="flex-1">
      <span className="block text-base font-semibold leading-tight text-amber-100 sm:text-[14px]">
        Wishlist on Steam
      </span>
      <span className="block text-xs text-amber-200/70 sm:text-[11px]">
        Full multiplayer is coming — get notified at launch
      </span>
      <span className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-200">
        <i className="fas fa-calendar-day text-[9px]" />
        Launching Aug 15, 2026
      </span>
    </span>
    <i className="fas fa-arrow-up-right-from-square text-xs text-amber-300/60 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-amber-200" />
  </a>
)

const LobbyBody: React.FC<LobbyBodyProps> = () => {
  const dispatch = useAppDispatch()
  const disableQuickJoinLocally = useAppSelector(
    (state) => state.global.disableQuickJoinLocally
  )
  const quickJoinQueueCount = useAppSelector(
    (state) => state.global.quickJoinQueueCount
  )
  const [newGameButtonEnabled, setNewGameButtonEnabled] = useState(true)
  const [isSinglePlayerModalOpen, setIsSinglePlayerModalOpen] = useState(false)
  const nav = useNavigate()

  useEffect(() => {
    dispatch(setState({ disableQuickJoinLocally: false }))
    setNewGameButtonEnabled(true)
  }, [dispatch])

  useEffect(() => {
    // Request initial status
    socket.emit("lobby.requestQuickJoinStatus")

    // Listen for status updates
    socket.on("lobby.quickJoinStatusUpdate", (data) => {
      dispatch(setState({ quickJoinStatus: data }))
    })

    // Queue size for the Multiplayer button. Sent to the whole lobby, so it
    // keeps ticking whether or not this player is the one waiting.
    socket.on("lobby.quickJoinQueueCount", ({ mapId, count }) => {
      if (mapId !== QUICK_JOIN_MAP_ID) return
      dispatch(setState({ quickJoinQueueCount: count }))
    })

    // Listen for game started event
    socket.on("lobby.quickJoinGameStarted", ({ gameDetails }) => {
      dispatch(setState({ myGame: gameDetails }))
      nav(`/game/${gameDetails.id}`)
    })

    return () => {
      socket.off("lobby.quickJoinStatusUpdate")
      socket.off("lobby.quickJoinQueueCount")
      socket.off("lobby.quickJoinGameStarted")
    }
  }, [dispatch, nav])

  const handleStartNewGameClick = () => {
    if (!newGameButtonEnabled || disableQuickJoinLocally) return
    setNewGameButtonEnabled(false)
    dispatch(setState({ disableQuickJoinLocally: true }))
    socket.emit("lobby.createGame", (gameId) => {
      setNewGameButtonEnabled(true)
      nav(`/newgame/${gameId}`)
    })
  }

  const quickJoin = () => {
    if (disableQuickJoinLocally) return
    dispatch(setState({ disableQuickJoinLocally: true }))
    socket.emit(
      "lobby.joinQuickJoinQueue",
      QUICK_JOIN_MAP_ID,
      getUserCreds().email
    )
  }

  const showDemoModePopup = () => {
    dispatch(
      addToastNotification({
        type: "general",
        messageType: "info",
        message:
          "Not available in demo mode. Buy complete game to enjoy multiplayer mode.",
      })
    )
  }

  const handleQuickJoinClick = () => {
    if (isDemoMode) {
      showDemoModePopup()
      return
    }
    quickJoin()
  }

  const handleCustomLobbyClick = () => {
    if (isDemoMode) {
      showDemoModePopup()
      return
    }
    handleStartNewGameClick()
  }

  const handleSinglePlayerClick = () => {
    setIsSinglePlayerModalOpen(true)
  }

  const handleHowToPlayClick = () => {
    nav("/how-to-play")
  }

  const handleChallengesClick = () => {
    nav("/challenges-list")
  }

  // Quitting only means something in the two builds that own their own window:
  // Electron (Steam), where electronApi is injected, and the Android app, where
  // there is no browser chrome to close the app with and the only other way out
  // is the system back gesture. In a browser tab the tab's own close button is
  // the quit button, so there is none here.
  const isElectron = typeof window !== "undefined" && !!window.electronApi?.quit
  const isAndroid = isAndroidApp()
  const showQuit = isElectron || isAndroid

  const handleQuitClick = () => {
    if (isElectron) window.electronApi?.quit?.()
    else exitApp()
  }

  const quickJoinDisabled =
    !isDemoMode &&
    (disableQuickJoinLocally || !featureFlags.allowQuickJoin)
  const customLobbyDisabled =
    !isDemoMode &&
    (!newGameButtonEnabled ||
      disableQuickJoinLocally ||
      !featureFlags.allowCustomLobby)

  const singlePlayerButton = (variant: "hero" | "tile") => (
    <ModeButton
      variant={variant}
      icon="fa-robot"
      label="Single Player"
      subtitle="Take on the bots at your own pace"
      accent="border-blue-400/40 bg-gradient-to-r from-blue-700 to-blue-800 focus:ring-blue-400"
      accentHover="hover:from-blue-600 hover:to-blue-700"
      onClick={handleSinglePlayerClick}
    />
  )

  const multiplayerButton = (variant: "hero" | "tile") => (
    <ModeButton
      variant={variant}
      icon="fa-bolt"
      label="Multiplayer"
      subtitle="Get matched with players online and start right away"
      accent="border-amber-400/40 bg-gradient-to-r from-amber-600 to-amber-700 focus:ring-amber-400"
      accentHover="hover:from-amber-500 hover:to-amber-600"
      onClick={handleQuickJoinClick}
      disabled={quickJoinDisabled}
      locked={isDemoMode}
      testId="lobby.quick-join-btn"
      badge={
        !isDemoMode && quickJoinQueueCount > 0 ? (
          <span
            className="inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-black/30 px-2 py-0.5 text-[11px] font-bold text-amber-100 ring-1 ring-white/20"
            data-testid="lobby.quick-join-queue-count"
            title={`${quickJoinQueueCount} ${
              quickJoinQueueCount > 1 ? "players are" : "player is"
            } waiting in the multiplayer queue`}
          >
            <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-green-400" />
            {quickJoinQueueCount} in queue
          </span>
        ) : undefined
      }
    />
  )

  // The mode we want a fresh player to hit first. Multiplayer is the draw, but
  // the demo cannot play it, so there it steps aside for single player.
  const heroButton = isDemoMode
    ? singlePlayerButton("hero")
    : multiplayerButton("hero")

  // The menu itself. Lifted out because the demo build lays the page out
  // differently around it (no ladder, no open games — just the store link).
  //
  // The logo rides at the top of this column rather than in a band above the
  // whole page: as a band it was a full-width strip that was ~90% empty air,
  // and the height for it came out of the two side columns, which are the ones
  // actually short of room. Here it sits in slack this column already had, and
  // lands directly above the buttons it introduces.
  //
  // On a short screen the column is centred with `safe` centring and keeps its
  // own scroll: a browser's toolbars can leave a landscape phone barely 250px
  // of page, and true centring would then push the logo off the top edge with
  // no way to reach it.
  const modeMenu = (
    <div className="flex w-[34rem] max-w-[90vw] flex-col gap-3 sm:order-2 sm:w-auto sm:min-w-0 sm:max-w-none sm:flex-[1.2] sm:gap-[7px] sm:overflow-y-auto sm:[justify-content:safe_center] xl:order-2 xl:w-auto xl:flex-none xl:justify-center">
      <img
        src={dealmasterLogo}
        alt="Deal Master"
        className="mx-auto mb-1 h-32 w-auto max-w-full select-none drop-shadow-[0_4px_24px_rgba(0,0,0,0.5)] sm:mb-0 sm:h-[44px] xl:mb-1 xl:h-20"
      />

      {heroButton}

      <div className="grid grid-cols-2 gap-3 sm:gap-[7px]">
        {isDemoMode ? multiplayerButton("tile") : singlePlayerButton("tile")}

        <ModeButton
          variant="tile"
          icon="fa-trophy"
          label="Challenges"
          accent="border-purple-400/40 bg-gradient-to-r from-purple-700 to-fuchsia-800 focus:ring-purple-400"
          accentHover="hover:from-purple-600 hover:to-fuchsia-700"
          onClick={handleChallengesClick}
        />

        <ModeButton
          variant="tile"
          icon="fa-users"
          label={newGameButtonEnabled ? "Custom Lobby" : "Creating..."}
          accent="border-emerald-400/40 bg-gradient-to-r from-emerald-600 to-emerald-800 focus:ring-emerald-400"
          accentHover="hover:from-emerald-500 hover:to-emerald-700"
          onClick={handleCustomLobbyClick}
          disabled={customLobbyDisabled}
          locked={isDemoMode}
          testId="start-new-game-btn"
        />

        <ModeButton
          variant="tile"
          icon="fa-circle-question"
          label="How to Play"
          accent="border-teal-400/40 bg-gradient-to-r from-teal-600 to-cyan-800 focus:ring-teal-400"
          accentHover="hover:from-teal-500 hover:to-cyan-700"
          onClick={handleHowToPlayClick}
        />
      </div>

      {/* Quitting is app chrome rather than a game mode, so it sits below
          the grid as a quiet link. */}
      {showQuit && (
        <button
          className="mt-1 flex items-center justify-center gap-2.5 rounded-lg border border-red-500/40 bg-red-600/70 py-2.5 text-sm font-semibold tracking-wide text-white transition-all duration-200 hover:border-red-500/60 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-400 sm:mt-0 sm:gap-[8px] sm:py-[7px] sm:text-[12px]"
          onClick={handleQuitClick}
        >
          <i className="fas fa-power-off text-xs" />
          Quit
        </button>
      )}
    </div>
  )

  return (
    <div className="flex w-full flex-1 flex-col sm:min-h-0 xl:min-h-0">
      {isDemoMode ? (
        // Nothing live to sit beside the menu in the demo, so the pair just
        // centres itself rather than pretending to fill a three-column page.
        <div className="flex flex-1 flex-col items-center gap-6 pl-[max(1rem,var(--safe-left))] pr-[max(1rem,var(--safe-right))] pb-[max(2rem,var(--safe-bottom))] pt-5 sm:min-h-0 sm:flex-row sm:items-center sm:justify-center sm:gap-[12px] sm:pl-[max(10px,var(--safe-left))] sm:pr-[max(10px,var(--safe-right))] sm:pb-[max(8px,var(--safe-bottom))] sm:pt-[6px] xl:min-h-0 xl:flex-row xl:items-center xl:justify-center xl:gap-14">
          {modeMenu}
          <WishlistCard />
        </div>
      ) : (
        // Three columns from xl: where you stand, what you can start, and what
        // is open right now. The widths are fractions of a pixel-capped frame
        // rather than fixed rem, because index.css drops the root font to 70%
        // under 1700px and 60% under 1300px — rem-sized columns shrink away
        // from the edges of the screen exactly when there is most room to fill.
        // Each side column owns its own scroll, so neither can push the other
        // under the fold.
        <div
          className={classNames(
            // Sides and bottom are `max(gutter, inset)` rather than the plain
            // gutter: the columns run right to the edges of the screen, which
            // on a landscape phone is where the camera cutout and the home
            // indicator sit. See utils/safeArea.ts — the page root keeps the
            // background full-bleed, only this frame moves in.
            "mx-auto grid w-full max-w-[1500px] flex-1 grid-cols-1 justify-items-center gap-6 pl-[max(1.5rem,var(--safe-left))] pr-[max(1.5rem,var(--safe-right))] pb-[max(2rem,var(--safe-bottom))] pt-5",
            // A phone gets those same columns side by side rather than the
            // stack: landscape leaves width to spare and no height at all, and
            // stacked it took three screenfuls of scrolling to reach the
            // ladder. Flex rather than grid here because the open-games panel
            // takes itself off the page when nothing is open — as a flex row
            // the two survivors simply share the width, where fixed grid tracks
            // would hold an empty third column open.
            "sm:flex sm:min-h-0 sm:flex-row sm:items-stretch sm:gap-[8px] sm:pl-[max(10px,var(--safe-left))] sm:pr-[max(10px,var(--safe-right))] sm:pb-[max(8px,var(--safe-bottom))] sm:pt-[6px]",
            // minmax(0,…) rather than a bare fr, so a long game title or
            // player name truncates inside its column instead of widening it.
            "xl:grid xl:min-h-0 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)_minmax(0,1.15fr)] xl:items-stretch xl:justify-items-stretch xl:gap-7"
          )}
        >
          {/* Source order is menu-first so the thing you came to do is at the
              top of a stacked (portrait/tablet) page; the side-by-side layouts
              re-order it into the middle, between the ladder and the open
              games. */}
          {modeMenu}

          <LobbyGameList
            className="sm:order-3 xl:order-3"
            onCreateGame={handleCustomLobbyClick}
            canCreateGame={!customLobbyDisabled}
          />

          <LobbyPlayerPanel className="sm:order-1 xl:order-1" />
        </div>
      )}

      <QuickJoinStatus />

      {/* Single Player Modal */}
      <SinglePlayerModal
        isOpen={isSinglePlayerModalOpen}
        onClose={() => setIsSinglePlayerModalOpen(false)}
      />
    </div>
  )
}

export default LobbyBody
